
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import ActivitySelect from '@/components/inputs/ActivitySelect.vue';
import ActivityService from '@/services/ActivityService';
import ApiHelper from '@/helpers/api-helper';
import { contarPedido, ESPERA_DE_PRECIOS, EsperaDePrecios } from '@/helpers/espera-de-precios';
import PriceHelper from '@/helpers/price-helper';
import Budget from '@/models/budget';
import moment from 'moment';
import {viajeYaEmpezado} from '@/helpers/tramo-helper';
import {
	Cambio, CambiosAlGuardar, etiquetaDelHueco, motivoPorFechas, motivoPorPersonas, Quitado, tarifasQueCambian,
	unidadDeActividad,
} from '@/helpers/ventana-al-guardar';

const PARTES = ['day_part_1', 'day_part_2', 'day_part_3'];

@Component({
	components: {
		ActivitySelect,
	},
})
export default class TabBudgetActivities extends Vue {
	// N-288 · 4A: GUARDAR espera mientras el catálogo de precios está en camino.
	@Inject({ from: ESPERA_DE_PRECIOS, default: null }) private readonly esperaDePrecios!: EsperaDePrecios | null;
	@Prop({ default: null, required: true }) private budget;
	@Prop({ default: null, required: true }) private days;
	@Prop({ default: null, required: true }) private people;
	@Prop({ default: null, required: true }) private students;
	@Prop({ default: null, required: false }) private errors;

	private activity_budget: any;
	private daysData: any;
	private activities: any[] = [];
	// N-285: cada día que ha estado en la parrilla durante esta edición, tal como lo dejó
	// el operador, aunque las fechas lo hayan sacado un momento del viaje.
	private diasDeLaSesion: any[] = [];
	// N-288 · T3: la parrilla tal como estaba guardada al abrir, para decir qué tarifa cambia de precio.
	private alAbrir: any[] = [];

	// N-264: cambiar los pasajeros NO rehace la parrilla: solo cambia el `people` de
	// cada media jornada. Rehacerla la devolvía a la foto de la API —deshaciendo lo que
	// el operador hubiera tocado— y con las tarifas como objetos, que el servidor no
	// reconoce: la actividad se borraba al guardar (presupuesto 106391).
	@Watch('people')
	people_change(value) {
		this.setPeople();
		this.setPrice();
		if (this.people && this.budget.travel_id) {
			this.getSelectorList();
		}
	}

	@Watch('days')
	days_change(value) {
		this.setDaysData();
	}

	constructor() {
		super();
		this.activity_budget = new Budget();
		this.daysData = 0;
	}

	mounted() {
		if (this.budget) {
			this.activity_budget = Budget.copy(this.budget);
			this.alAbrir = JSON.parse(JSON.stringify(this.budget.activities || []));
		}
		if (this.days) {
			this.setDaysData();
		}
		if (this.people && this.students) {
			this.setPrice();
		}
		if (this.people && this.budget.travel_id) {
			this.getSelectorList();
		}
	}

	getSelectorList() {
		const pedidoPara = this.people;
		contarPedido(this.esperaDePrecios, ActivityService.getSelectorList(null, { travel_id: this.budget.travel_id, people: pedidoPara })
			.then(res => {
				// N-286: al teclear «29» se pide también el catálogo de «2»; si esa respuesta
				// llega la última, sus tarifas (de otro tramo) no deben pisar las buenas.
				if (pedidoPara !== this.people) {
					return;
				}
				if (res.status === 200) {
					this.activities = res.data.elements;
				}
			})
			.catch(error => {
				ApiHelper.handleErrors(error, {}, this.$bus);
			}));
	}

	/**
	 * N-264: al cambiar las fechas, cada día que sigue dentro del viaje conserva lo que
	 * tiene AHORA —el mismo objeto, que es el `value` de su selector—, no la foto de la
	 * API. Solo un día que aún no estaba en la parrilla se rellena desde lo guardado. Los
	 * que se salen del viaje se caen, y el servidor borra sus actividades (N-248).
	 *
	 * N-285: con unas fechas a medio elegir no se toca la parrilla. Retrasar la llegada
	 * deja la salida vacía y `days` deja de ser un número: rehacerla entonces la dejaba
	 * en CERO días, y al elegir la salida volvía la foto de la API sin lo añadido en la
	 * sesión (Londres del IES Sem Tob, presupuesto 107643). Y un día que se sale y vuelve
	 * antes de guardar —acortar y alargar— vuelve como lo dejó el operador.
	 */
	setDaysData() {
		const numeroDeDias = Number(this.days);
		if (!Number.isInteger(numeroDeDias) || numeroDeDias < 1) {
			return;
		}
		if (Array.isArray(this.daysData)) {
			this.daysData.forEach((dia, indice) => {
				this.diasDeLaSesion[indice] = dia;
			});
		}
		const dias: any[] = [];
		for (let i = 0; i < numeroDeDias; i++) {
			if (this.diasDeLaSesion[i]) {
				dias.push(this.diasDeLaSesion[i]);
				continue;
			}
			let existDay = null;
			for (const activity of this.activity_budget.activities) {
				if (activity.day == i) {
					existDay = activity;
				}
			}
			if (existDay !== null) {
				dias.push(this.copy(existDay));
			} else {
				dias.push({});
			}
		}
		this.daysData = dias;
		// Un día que vuelve no se enteró de los pasajeros que cambiaron mientras estaba fuera.
		this.setPeople();
		// this.setPrice();
	}

	/** N-264: los pasajeros nuevos, en cada media jornada de la parrilla tal como está. */
	setPeople() {
		if (!Array.isArray(this.daysData)) {
			return;
		}
		for (const day of this.daysData) {
			for (const part of ['day_part_1', 'day_part_2', 'day_part_3']) {
				if (day[part]) {
					day[part].people = this.people;
				}
			}
		}
	}

	setPrice() {
		let total = 0;
		for (const day of this.daysData) {
			if (day.day_part_1 && day.day_part_1.activity_id) {
				total += this.getPricesAndExtras(day.day_part_1);
			}
			if (day.day_part_2 && day.day_part_2.activity_id) {
				total += this.getPricesAndExtras(day.day_part_2);
			}
			if (day.day_part_3 && day.day_part_3.activity_id) {
				total += this.getPricesAndExtras(day.day_part_3);
			}
		}
		this.$emit('setPrice', total);
	}

	/**
	 * M-16: lo que le cuesta a cada alumno el día completo. Cada tarifa es una
	 * LÍNEA: las de GRUPO se reparten entre los pasajeros y se cobran en euros
	 * enteros, la MISMA regla que aplica el motor al guardar
	 * (`BudgetActivitiesService::selectPrice`) — y el divisor es `people`, que es
	 * justo lo que este formulario ya ENVÍA al servidor.
	 */
	getPricesAndExtras(day) {
		return (
			PriceHelper.activityLinesTotal(day.prices_to_display, this.people) +
			PriceHelper.activityLinesTotal(day.extras, this.people)
		);
	}

	copy(model) {
		return {
			daypart: model.daypart,
			day_part_1: {
				day: model.day_part_1.day,
				people: this.people,
				daypart: 1,
				activity_id: model.day_part_1.activity_id,
				budget_activity_id: model.day_part_1.budget_activity_id,
				prices_to_display: model.day_part_1.prices_to_display,
				prices: model.day_part_1.prices,
				extras: model.day_part_1.extras,
			},
			day_part_2: {
				day: model.day_part_2.day,
				people: this.people,
				daypart: 2,
				activity_id: model.day_part_2.activity_id,
				budget_activity_id: model.day_part_2.budget_activity_id,
				prices_to_display: model.day_part_2.prices_to_display,
				prices: model.day_part_2.prices,
				extras: model.day_part_2.extras,
			},
			day_part_3: {
				day: model.day_part_3.day,
				people: this.people,
				daypart: 3,
				activity_id: model.day_part_3.activity_id,
				budget_activity_id: model.day_part_3.budget_activity_id,
				prices_to_display: model.day_part_3.prices_to_display,
				prices: model.day_part_3.prices,
				extras: model.day_part_3.extras,
			},
		};
	}

	/**
	 * N-288 · T3 (2A · 3A) · Para la ventana de GUARDAR, con el catálogo del número de ahora:
	 * - se quita la actividad sin tarifa obligatoria para el grupo («llega a 55»), la que queda fuera de
	 *   su ventana de fechas ese día («solo hasta el 31/05/2027»; hoy se guardaba y se cobraba) y lo de los
	 *   días que sobran al acortar (N-248). La ventana de fechas NO se mira en un viaje que ya empezó
	 *   (decisión de Bernardo, 8-oct, como el alojamiento): las vigencias se re-fechan con el tiempo y un
	 *   viaje viejo perdería sus actividades al corregir cualquier dato;
	 * - cambia de precio la tarifa obligatoria que pasó a la del tramo nuevo, y los extras (N-286).
	 * `huecos`: las medias jornadas que hay que quitar del envío (las de los días que sobran ya no van).
	 * Una actividad que no está en el catálogo (despublicada) se queda como está: decide el servidor (N-264).
	 */
	cambiosAlGuardar(): CambiosAlGuardar & { huecos: any[] } {
		const quitan: Quitado[] = [];
		const cambian: Cambio[] = [];
		const huecos: any[] = [];
		if (!this.activities.length || !Array.isArray(this.daysData)) {
			return { quitan, cambian, huecos };
		}
		const personas = Number(this.people);
		const selectores: any[] = (this.$refs as any).selectores || [];
		const inicio = moment(this.budget.start_date);
		const mirarFechas = inicio.isValid() && !viajeYaEmpezado(this.budget.start_date);
		this.daysData.forEach((dia, indice) => {
			const fecha = mirarFechas ? inicio.clone().add(indice, 'days').format('YYYY-MM-DD') : '';
			const selector = selectores.find((uno) => uno.day === indice);
			for (const parte of PARTES) {
				const hueco = dia[parte];
				const actividad = hueco && hueco.activity_id ? this.activities.find((una) => una.id == hueco.activity_id) : null;
				if (!actividad) {
					continue;
				}
				const etiqueta = etiquetaDelHueco(indice, parte, actividad.name);
				const fuera = motivoPorFechas(actividad.availability_start_date, actividad.availability_end_date, fecha);
				if (!(actividad.prices || []).length || fuera) {
					quitan.push(fuera && (actividad.prices || []).length
						? { texto: etiqueta, motivo: fuera, causa: 'fechas' }
						: { texto: etiqueta, motivo: motivoPorPersonas(actividad.people_range, personas), causa: 'personas' });
					huecos.push({ day: indice, parte });
					continue;
				}
				const guardado = this.huecoAlAbrir(indice, parte, hueco.activity_id);
				for (const cambio of tarifasQueCambian(guardado ? guardado.prices_to_display : [], hueco.prices_to_display)) {
					cambian.push({ texto: etiqueta + ', ' + cambio.ahora.name, antes: cambio.antes.price, ahora: cambio.ahora.price,
						unidad: unidadDeActividad(cambio.ahora) });
				}
				const extras = selector ? selector.extrasQueCambian(parte) : { quitados: [], cambiados: [] };
				for (const extra of extras.quitados) {
					quitan.push({ texto: etiqueta + ', ' + extra.name, motivo: '', causa: 'personas' });
				}
				for (const cambio of extras.cambiados) {
					cambian.push({ texto: etiqueta + ', ' + cambio.ahora.name, antes: cambio.antes.price, ahora: cambio.ahora.price,
						unidad: unidadDeActividad(cambio.ahora) });
				}
			}
		});
		return { quitan: quitan.concat(this.loDeLosDiasQueSobran()), cambian, huecos };
	}

	/** La media jornada guardada de ese día con esa actividad, o null. */
	huecoAlAbrir(indice, parte, actividadId) {
		const dia = this.alAbrir.find((uno) => uno.day == indice);
		return dia && dia[parte] && dia[parte].activity_id == actividadId ? dia[parte] : null;
	}

	/** N-248: lo de los días que se salen del viaje al acortarlo (guardado o añadido en la sesión). */
	loDeLosDiasQueSobran(): Quitado[] {
		const numeroDeDias = this.daysData.length;
		const indices: number[] = [];
		for (const dia of this.alAbrir) {
			if (dia.day >= numeroDeDias && indices.indexOf(dia.day) < 0) {
				indices.push(dia.day);
			}
		}
		this.diasDeLaSesion.forEach((dia, indice) => {
			if (dia && indice >= numeroDeDias && indices.indexOf(indice) < 0) {
				indices.push(indice);
			}
		});
		const motivo = 'el viaje ahora es de ' + numeroDeDias + (numeroDeDias === 1 ? ' día' : ' días');
		const quitan: Quitado[] = [];
		for (const indice of indices.sort((a, b) => a - b)) {
			const dia = this.diasDeLaSesion[indice] || this.alAbrir.find((uno) => uno.day == indice);
			for (const parte of PARTES) {
				const hueco = dia[parte];
				if (hueco && hueco.activity_id) {
					const actividad = this.activities.find((una) => una.id == hueco.activity_id);
					// La copia de un día (`copy`) no lleva el nombre: el de lo guardado, si es la misma.
					const guardado = this.huecoAlAbrir(indice, parte, hueco.activity_id);
					const nombre = actividad ? actividad.name
						: (hueco.activity_name || (guardado && guardado.activity_name) || 'Actividad que ya no está en el catálogo');
					quitan.push({ texto: etiquetaDelHueco(indice, parte, nombre), motivo, causa: 'dias' });
				}
			}
		}
		return quitan;
	}

	async validate() {
		this.$emit('input', this.daysData);
		return true
	}
}
